import { Box, Checkbox, Divider, FormControlLabel, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import { useEffect } from "react"
import { Controller, FormProvider, type UseFormProps, useForm } from "react-hook-form"

import { StatusFilter } from "@/components/common/Filters"
import { SearchFilterDrawer } from "@/components/common/SearchFilterDrawer/SearchFilterDrawer"
import type { ContactQueryableFields } from "@/graphql/codegen/graphql"
import { useGetLocations } from "@/hooks/useGetLocations"
import { DepartmentFilter } from "@/screens/Companies/components/filters/contact/DepartmentFilter"
import { LocationsFilter } from "@/screens/Companies/components/filters/contact/LocationsFilter"
import { ContactSubscriptionFilter } from "@/screens/Companies/components/filters/contact/SubscriptionSearchFilter"
import { useContactSubscriptionsStore } from "@/screens/Companies/stores/useContactSubscriptionsStore"
import { ContactStatus } from "@/screens/Contacts/types"

interface FilterDrawerProps {
  showFilterDrawer: boolean
  toggleDrawer: (open: boolean) => void
  companyId: string
}

const IS_ACTIVE_FIELD_NAME = "isActive"

export type FormValues = {
  isActive: ContactStatus | ContactStatus[]
  isPrimaryContact: boolean
  companyDepartments: string[]
  contactSubscriptions: string[]
  locationIds: string[]
} & ContactQueryableFields

const ContactsFilterDrawer: FC<FilterDrawerProps> = ({ toggleDrawer, showFilterDrawer, companyId }) => {
  const navigate = useNavigate({ from: "/app/companies/$companyId/edit/contacts" })
  const { isActive, isPrimaryContact, companyDepartments, contactSubscriptions, locationIds } = useSearch({
    from: "/app/companies/$companyId/edit/_layout/contacts",
    strict: true,
  })

  const { locations } = useGetLocations(companyId)

  const subscriptions = useContactSubscriptionsStore((state) => state.contactSubscriptions)
  const contactSubscriptionFetching = useContactSubscriptionsStore((state) => state.fetching)
  const fetchContactSubscriptions = useContactSubscriptionsStore((state) => state.fetchContactSubscriptions)

  const methods = useForm<FormValues>({
    values: {
      [IS_ACTIVE_FIELD_NAME]: isActive !== undefined ? (isActive ? ContactStatus.Active : ContactStatus.Inactive) : [],
      isPrimaryContact: isPrimaryContact ?? false,
      companyDepartments: companyDepartments ?? [],
      contactSubscriptions: contactSubscriptions ?? [],
      locationIds: locationIds ?? [],
    },
  } as UseFormProps<FormValues>)

  useEffect(() => {
    if (!contactSubscriptionFetching && subscriptions.length === 0) {
      fetchContactSubscriptions()
    }
  }, [contactSubscriptionFetching, subscriptions.length, fetchContactSubscriptions]) // fetching Contact Subscriptions

  const handleClearFilters = () => {
    navigate({
      search: (prev: Record<string, any>) => ({
        ...prev,
        [IS_ACTIVE_FIELD_NAME]: true,
        isPrimaryContact: undefined,
        companyDepartments: undefined,
        contactSubscriptions: undefined,
        locationIds: undefined,
      }),
      replace: true,
    })
  }

  const handleFilterChange = (name: keyof FormValues, value: unknown) => {
    if (name === IS_ACTIVE_FIELD_NAME) {
      const isActiveValue =
        (value as ContactStatus[]).length === 1 ? (value as ContactStatus[])[0] === ContactStatus.Active : undefined

      navigate({
        search: (prev: Record<string, any>) => ({
          ...prev,
          [IS_ACTIVE_FIELD_NAME]: isActiveValue,
        }),
        replace: true,
      })
    } else {
      navigate({
        search: (prev: Record<string, any>) => {
          const val = Array.isArray(value) && value.length === 0 ? undefined : value

          return {
            ...prev,
            [name]: val || undefined,
          }
        },
        replace: true,
      })
    }
  }

  return (
    <SearchFilterDrawer
      toggleDrawer={() => toggleDrawer(!showFilterDrawer)}
      open={showFilterDrawer}
      clear={handleClearFilters}
    >
      <FormProvider {...methods}>
        <Box className='min-w-80 flex-none'>
          <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
            Contact Status
          </Typography>
          <Divider />
          <StatusFilter
            name={IS_ACTIVE_FIELD_NAME}
            data={Object.values(ContactStatus)}
            control={methods.control}
            onFilterChange={handleFilterChange}
          />
        </Box>

        <Box className='min-w-80 flex-none'>
          <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
            Contact Type
          </Typography>
          <Divider />
          <Controller
            name='isPrimaryContact'
            control={methods.control}
            render={({ field }) => (
              <Box className='flex flex-col px-8 py-4'>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked)
                        handleFilterChange(field.name, e.target.checked)
                      }}
                    />
                  }
                  label='Primary'
                />
              </Box>
            )}
          />
        </Box>

        <Box className='grow overflow-hidden'>
          <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
            Subscriptions
          </Typography>
          <Divider />
          <ContactSubscriptionFilter subscriptions={subscriptions} onFilterChange={handleFilterChange} />
        </Box>

        <Box className='grow overflow-hidden'>
          <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
            Department
          </Typography>
          <Divider />
          <DepartmentFilter onFilterChange={handleFilterChange} />
        </Box>

        <Box className='grow overflow-hidden'>
          <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
            Locations
          </Typography>
          <Divider />
          <LocationsFilter locations={locations} onFilterChange={handleFilterChange} />
        </Box>
      </FormProvider>
    </SearchFilterDrawer>
  )
}

export default ContactsFilterDrawer
